import { aiImageAnalysis } from './ai-image-analysis'

interface VerificationResult {
  pass: boolean
  score: number
  messages: string[]
  details: VerificationDetails
}

interface VerificationDetails {
  hasFace: boolean
  faceCount: number
  hasBadge: boolean
  badgeScore: number
  badgePosition: 'left' | 'right' | 'center' | 'unknown'
  badgeColor: string
  hasLogo: boolean
  logoScore: number
  isUnique: boolean
  similarityScore: number
  overallQuality: number
}

interface AvatarRecord {
  userId: string
  hash: string
  faceFeatures: number[]
  timestamp: number
  verified: boolean
}

export class AIAvatarVerification {
  private verifiedAvatars: Map<string, AvatarRecord> = new Map()
  private verificationHistory: Map<string, VerificationResult> = new Map()
  private lastVerificationTime: Map<string, number> = new Map()
  private verificationCooldown = 300000

  private badgePatterns = {
    color: {
      blue: { r: [50, 150], g: [100, 200], b: [200, 255] },
      cyan: { r: [0, 100], g: [150, 255], b: [150, 255] }
    },
    shape: 'circle',
    logoText: ['伴', 'BAN', 'XIE']
  }

  async verifyAvatar(imageData: string, userId?: string): Promise<VerificationResult> {
    if (userId && this.isWithinCooldown(userId)) {
      const cachedResult = this.verificationHistory.get(userId)
      if (cachedResult) {
        return cachedResult
      }
    }

    const details: VerificationDetails = {
      hasFace: false,
      faceCount: 0,
      hasBadge: false,
      badgeScore: 0,
      badgePosition: 'unknown',
      badgeColor: '',
      hasLogo: false,
      logoScore: 0,
      isUnique: true,
      similarityScore: 0,
      overallQuality: 0
    }

    const messages: string[] = []
    let totalScore = 0

    const [faces, objects, caption, imageInfo] = await Promise.all([
      aiImageAnalysis.detectFaces(imageData),
      aiImageAnalysis.detectObjects(imageData),
      aiImageAnalysis.generateCaption(imageData),
      aiImageAnalysis.getImageInfo(imageData)
    ])

    details.hasFace = faces.faces.length > 0
    details.faceCount = faces.faces.length

    if (!details.hasFace) {
      messages.push('❌ 未检测到人脸')
      totalScore -= 50
    } else if (details.faceCount !== 1) {
      messages.push(`❌ 检测到${details.faceCount}个人脸，只允许单人头像`)
      totalScore -= 30
    } else {
      messages.push('✅ 检测到单人头像')
      totalScore += 30
      details.overallQuality += 30
    }

    const badgeDetection = this.detectBadge(objects, imageInfo, caption)
    details.hasBadge = badgeDetection.found
    details.badgeScore = badgeDetection.score
    details.badgePosition = badgeDetection.position
    details.badgeColor = badgeDetection.color
    details.hasLogo = badgeDetection.hasLogo
    details.logoScore = badgeDetection.logoScore

    if (details.hasBadge && details.hasLogo) {
      messages.push(`✅ 检测到佩戴胸牌（${details.badgeColor}色，${details.badgePosition}侧）`)
      totalScore += 50
      details.overallQuality += 40
    } else if (details.hasBadge) {
      messages.push('⚠️ 检测到胸牌，但未识别到LOGO')
      totalScore += 20
      details.overallQuality += 15
    } else {
      messages.push('❌ 未检测到佩戴胸牌')
      totalScore -= 40
    }

    if (userId) {
      const uniqueness = await this.checkUniqueness(imageData, userId)
      details.isUnique = uniqueness.isUnique
      details.similarityScore = uniqueness.similarityScore

      if (!details.isUnique) {
        messages.push('❌ 头像与其他用户重复，可能存在盗用')
        totalScore -= 30
      } else {
        messages.push('✅ 头像验证通过，未发现重复')
        totalScore += 20
        details.overallQuality += 20
      }

      if (details.isUnique && details.hasBadge && details.hasLogo && details.hasFace && details.faceCount === 1) {
        this.cacheVerification(userId, {
          pass: true,
          score: totalScore,
          messages,
          details
        })
      }
    }

    totalScore = Math.max(-100, Math.min(100, totalScore))
    details.overallQuality = Math.max(0, Math.min(100, details.overallQuality))

    const result: VerificationResult = {
      pass: totalScore >= 60,
      score: totalScore,
      messages,
      details
    }

    if (userId) {
      this.verificationHistory.set(userId, result)
      this.lastVerificationTime.set(userId, Date.now())
    }

    return result
  }

  private detectBadge(objects: any, imageInfo: any, caption: any): {
    found: boolean
    score: number
    position: 'left' | 'right' | 'center' | 'unknown'
    color: string
    hasLogo: boolean
    logoScore: number
  } {
    let score = 0
    let found = false
    let position: 'left' | 'right' | 'center' | 'unknown' = 'unknown'
    let color = ''
    let hasLogo = false
    let logoScore = 0

    const shirtBoxes = objects.boxes.filter((box: any) => 
      box.label === 'shirt' || box.label === 'person' || box.label === 'jacket'
    )

    if (shirtBoxes.length > 0) {
      const shirtBox = shirtBoxes[0]
      const shirtCenterX = (shirtBox.x1 + shirtBox.x2) / 2
      const shirtCenterY = (shirtBox.y1 + shirtBox.y2) / 2

      const potentialBadges = objects.boxes.filter((box: any) => {
        const boxCenterX = (box.x1 + box.x2) / 2
        const boxCenterY = (box.y1 + box.y2) / 2

        const isNearShirt = Math.abs(boxCenterX - shirtCenterX) < imageInfo.width * 0.4 &&
                          Math.abs(boxCenterY - shirtCenterY) < imageInfo.height * 0.3

        const sizeScore = (box.x2 - box.x1) * (box.y2 - box.y1)
        const isReasonableSize = sizeScore > imageInfo.width * imageInfo.height * 0.005 &&
                               sizeScore < imageInfo.width * imageInfo.height * 0.05

        return isNearShirt && isReasonableSize
      })

      if (potentialBadges.length > 0) {
        const badge = potentialBadges[0]
        found = true
        score += 30

        const badgeCenterX = (badge.x1 + badge.x2) / imageInfo.width
        if (badgeCenterX < 0.35) {
          position = 'left'
        } else if (badgeCenterX > 0.65) {
          position = 'right'
        } else {
          position = 'center'
        }
        score += 10

        const colorDetection = this.detectBadgeColor(imageData, badge)
        color = colorDetection.color
        score += colorDetection.score

        const logoDetection = this.detectLogoInCaption(caption.caption)
        hasLogo = logoDetection.found
        logoScore = logoDetection.score
        score += logoDetection.score

        if (badge.label === 'badge' || badge.label === 'sign') {
          score += 20
        }
      }
    }

    if (caption.caption.toLowerCase().includes('badge') || 
        caption.caption.toLowerCase().includes('logo') ||
        caption.caption.toLowerCase().includes('胸牌') ||
        caption.caption.toLowerCase().includes('sign')) {
      score += 15
      hasLogo = true
      logoScore += 0.3
    }

    if (caption.caption.includes('伴') || caption.caption.includes('Ban') || caption.caption.includes('Xie')) {
      score += 20
      hasLogo = true
      logoScore += 0.4
    }

    return {
      found: score >= 30,
      score: Math.min(100, score),
      position,
      color,
      hasLogo: logoScore >= 0.5,
      logoScore: Math.min(1, logoScore)
    }
  }

  private detectBadgeColor(imageData: string, badgeBox: any): { color: string; score: number } {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx!.drawImage(img, 0, 0)

        const x = Math.floor((badgeBox.x1 + badgeBox.x2) / 2)
        const y = Math.floor((badgeBox.y1 + badgeBox.y2) / 2)

        try {
          const pixel = ctx!.getImageData(x, y, 1, 1).data
          const r = pixel[0]
          const g = pixel[1]
          const b = pixel[2]

          if (r >= 50 && r <= 150 && g >= 100 && g <= 200 && b >= 200 && b <= 255) {
            resolve({ color: '蓝色', score: 30 })
          } else if (r >= 0 && r <= 100 && g >= 150 && g <= 255 && b >= 150 && b <= 255) {
            resolve({ color: '青色', score: 25 })
          } else if (r >= 200 && g >= 200 && b >= 200) {
            resolve({ color: '白色', score: 10 })
          } else if (r >= 150 && g >= 50 && b >= 50) {
            resolve({ color: '红色', score: 15 })
          } else {
            resolve({ color: '其他', score: 5 })
          }
        } catch {
          resolve({ color: '未知', score: 0 })
        }
      }

      img.onerror = () => {
        resolve({ color: '未知', score: 0 })
      }

      img.src = imageData
    })
  }

  private detectLogoInCaption(caption: string): { found: boolean; score: number } {
    let score = 0

    if (caption.includes('伴')) {
      score += 0.5
    }

    if (caption.toLowerCase().includes('logo')) {
      score += 0.2
    }

    if (caption.toLowerCase().includes('badge')) {
      score += 0.2
    }

    if (caption.toLowerCase().includes('sign')) {
      score += 0.1
    }

    return {
      found: score >= 0.5,
      score: Math.min(1, score)
    }
  }

  private async checkUniqueness(imageData: string, userId: string): Promise<{ isUnique: boolean; similarityScore: number }> {
    const newHash = this.calculateImageHash(imageData)
    const newFeatures = await this.extractFaceFeatures(imageData)

    let maxSimilarity = 0
    let isUnique = true

    for (const [otherUserId, record] of this.verifiedAvatars) {
      if (otherUserId !== userId) {
        const hashSimilarity = this.compareHashes(newHash, record.hash)
        const featureSimilarity = this.compareFeatures(newFeatures, record.faceFeatures)

        const overallSimilarity = hashSimilarity * 0.3 + featureSimilarity * 0.7

        if (overallSimilarity > maxSimilarity) {
          maxSimilarity = overallSimilarity
        }

        if (overallSimilarity > 0.85) {
          isUnique = false
          break
        }
      }
    }

    if (isUnique) {
      this.verifiedAvatars.set(userId, {
        userId,
        hash: newHash,
        faceFeatures: newFeatures,
        timestamp: Date.now(),
        verified: true
      })
    }

    return {
      isUnique,
      similarityScore: Math.round(maxSimilarity * 100) / 100
    }
  }

  private calculateImageHash(imageData: string): string {
    let hash = 0
    for (let i = 0; i < imageData.length; i++) {
      const char = imageData.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(36)
  }

  private compareHashes(hash1: string, hash2: string): number {
    let matches = 0
    const minLength = Math.min(hash1.length, hash2.length)
    
    for (let i = 0; i < minLength; i++) {
      if (hash1[i] === hash2[i]) {
        matches++
      }
    }
    
    return matches / minLength
  }

  private async extractFaceFeatures(imageData: string): Promise<number[]> {
    // V1.3.63 本地无真实 CV 人脸特征提取能力：原先 Math.random() 生成 128 维特征，
    // 导致同一图片每次特征不同、相似度比对完全随机。现改为对输入图片字符串做确定性
    // 哈希派生（每维一个 djb2 带种子哈希，按步长 128 采样，字符位置参与混合），
    // 归一化到 [0,1]。同一输入特征向量一致，不同输入可区分；仅供本地占位比对，
    // 非真实人脸 embedding。返回类型 number[] 与比对逻辑保持不变。
    const DIMENSIONS = 128
    const features: number[] = new Array(DIMENSIONS)

    for (let dim = 0; dim < DIMENSIONS; dim++) {
      // 各维度使用不同初始种子，采样不同位置的字符，保证每个字符恰好影响一个维度
      let hash = (5381 + dim * 33) | 0
      for (let i = dim; i < imageData.length; i += DIMENSIONS) {
        hash = (((hash << 5) + hash) + imageData.charCodeAt(i) + (i & 0xff)) | 0
      }
      features[dim] = (hash >>> 0) / 4294967295
    }

    return features
  }

  private compareFeatures(features1: number[], features2: number[]): number {
    if (features1.length !== features2.length) {
      return 0
    }

    let sumSquaredDiff = 0
    for (let i = 0; i < features1.length; i++) {
      sumSquaredDiff += Math.pow(features1[i] - features2[i], 2)
    }

    const distance = Math.sqrt(sumSquaredDiff)
    return Math.max(0, 1 - distance / Math.sqrt(features1.length))
  }

  private isWithinCooldown(userId: string): boolean {
    const lastTime = this.lastVerificationTime.get(userId)
    if (!lastTime) {
      return false
    }
    return Date.now() - lastTime < this.verificationCooldown
  }

  private cacheVerification(userId: string, result: VerificationResult) {
    this.verifiedAvatars.set(userId, {
      userId,
      hash: '',
      faceFeatures: [],
      timestamp: Date.now(),
      verified: result.pass
    })
  }

  getVerificationStatus(userId: string): VerificationResult | undefined {
    return this.verificationHistory.get(userId)
  }

  invalidateVerification(userId: string) {
    this.verifiedAvatars.delete(userId)
    this.verificationHistory.delete(userId)
    this.lastVerificationTime.delete(userId)
  }

  getStats() {
    return {
      totalVerifications: this.verificationHistory.size,
      verifiedUsers: this.verifiedAvatars.size,
      cooldownSeconds: this.verificationCooldown / 1000
    }
  }
}

export const aiAvatarVerification = new AIAvatarVerification()